import { useState, useRef } from 'react';
import { cn } from '@/lib/utils';

interface ImageHoverPreviewProps {
  src: string;
  alt: string;
  className?: string;
  previewSize?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

export const ImageHoverPreview: React.FC<ImageHoverPreviewProps> = ({
  src,
  alt,
  className,
  previewSize = 'md',
  children
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  // 根据预览大小设置尺寸 - 竖幅6英寸明信片比例 (4:6)
  const sizeClasses = {
    sm: 'w-56 h-84',
    md: 'w-72 h-108', 
    lg: 'w-96 h-144'
  };

  const handleMouseEnter = (event: React.MouseEvent) => {
    setShowPreview(true);
    updatePreviewPosition(event);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    updatePreviewPosition(event);
  };

  const handleMouseLeave = () => {
    setShowPreview(false);
  };

  const updatePreviewPosition = (event: React.MouseEvent) => {
    const mouseX = event.clientX;
    const mouseY = event.clientY;
    const offset = 20;
    
    // 获取视窗尺寸
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // 预览框尺寸（根据previewSize计算） - 竖幅比例
    const previewWidth = previewSize === 'sm' ? 224 : previewSize === 'lg' ? 384 : 288;
    const previewHeight = previewSize === 'sm' ? 336 : previewSize === 'lg' ? 576 : 432;
    
    let x = mouseX + offset;
    let y = mouseY + offset;
    
    // 防止预览框超出右边界
    if (x + previewWidth > viewportWidth) {
      x = mouseX - previewWidth - offset;
    }
    
    // 防止预览框超出下边界
    if (y + previewHeight > viewportHeight) {
      y = mouseY - previewHeight - offset;
    }
    
    // 防止预览框超出左边界
    if (x < 0) {
      x = offset;
    }
    
    // 防止预览框超出上边界
    if (y < 0) {
      y = offset;
    }
    
    setPreviewPosition({ x, y });
  };

  return (
    <div
      ref={containerRef}
      className={cn("relative inline-block cursor-pointer", className)}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      
      {/* 悬浮预览 */}
      {showPreview && (
        <div
          ref={previewRef}
          className={cn(
            "fixed z-[9999] pointer-events-none",
            "bg-white border border-gray-200 rounded-lg shadow-2xl",
            "overflow-hidden transition-opacity duration-200",
            sizeClasses[previewSize]
          )}
          style={{
            left: `${previewPosition.x}px`,
            top: `${previewPosition.y}px`,
          }}
        >
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover bg-gray-50"
            loading="eager"
            onError={(e) => {
              // 图片加载失败时显示占位符
              e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04IDlIMTZWMTVIOFY5WiIgZmlsbD0iI0Q0RDREOCIvPgo8Y2lyY2xlIGN4PSIxMCIgY3k9IjExIiByPSIxIiBmaWxsPSIjOUM5Q0E2Ii8+CjxwYXRoIGQ9Ik0xMyAxM0wxNSAxMUwxNiAxM0gxM1oiIGZpbGw9IiM5QzlDQTYiLz4KPC9zdmc+';
            }}
          />
        </div>
      )}
    </div>
  );
};